import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api.service';

type PartnerRow = {
  id: string;
  code: string;
  name: string;
  webhookUrl: string | null;
  hasWebhookSecret: boolean;
  hasApiKey: boolean;
  notifyOn: 'APPROVED' | 'DISBURSED';
  defaultBranchId: string | null;
  defaultBranch?: { id: string; code: string; name: string } | null;
  productMap: Record<string, string>;
  isActive: boolean;
  connection?: {
    ingestUrl: string;
    ingestPath: string;
    statusPath: string;
    authHeader: string;
    events: string[];
    signatureHeader: string;
    apiBaseUrl: string;
  };
};

export function PartnersIntegrationsConfig() {
  const [partners, setPartners] = useState<PartnerRow[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PartnerRow | null>(null);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    code: '',
    name: '',
    webhookUrl: '',
    webhookSecret: '',
    notifyOn: 'DISBURSED' as 'APPROVED' | 'DISBURSED',
    defaultBranchId: '',
    personalProductId: '',
    isActive: true,
  });

  const apiBase = useMemo(
    () => (import.meta as any).env?.VITE_API_URL?.replace(/\/$/, '') || window.location.origin,
    [],
  );

  const loadList = async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, br, prods] = await Promise.all([
        apiService.listAdminPartners(),
        apiService.getBranches(true),
        apiService.getLoanProducts({ activeOnly: true }),
      ]);
      setPartners(list);
      setBranches(br);
      setProducts(Array.isArray(prods) ? prods : []);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id: string) => {
    setError(null);
    setMessage(null);
    setRevealedKey(null);
    try {
      const [d, dels] = await Promise.all([
        apiService.getAdminPartner(id),
        apiService.listAdminPartnerDeliveries(id, 40),
      ]);
      setDetail(d);
      setDeliveries(dels);
      setSelectedId(id);
      setCreating(false);
      setForm({
        code: d.code,
        name: d.name,
        webhookUrl: d.webhookUrl ?? '',
        webhookSecret: '',
        notifyOn: d.notifyOn,
        defaultBranchId: d.defaultBranchId ?? '',
        personalProductId: d.productMap?.PERSONAL ?? '',
        isActive: d.isActive,
      });
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const startCreate = () => {
    setCreating(true);
    setSelectedId(null);
    setDetail(null);
    setDeliveries([]);
    setRevealedKey(null);
    setForm({
      code: '',
      name: '',
      webhookUrl: '',
      webhookSecret: '',
      notifyOn: 'DISBURSED',
      defaultBranchId: branches[0]?.id ?? '',
      personalProductId: products[0]?.id ?? '',
      isActive: true,
    });
  };

  const productMapFromForm = (): Record<string, string> => {
    const map: Record<string, string> = { ...(detail?.productMap ?? {}) };
    if (form.personalProductId) map.PERSONAL = form.personalProductId;
    else delete map.PERSONAL;
    return map;
  };

  const saveCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await apiService.createAdminPartner({
        code: form.code,
        name: form.name,
        webhookUrl: form.webhookUrl || undefined,
        webhookSecret: form.webhookSecret || undefined,
        notifyOn: form.notifyOn,
        defaultBranchId: form.defaultBranchId || undefined,
        productMap: productMapFromForm(),
        isActive: form.isActive,
      });
      setRevealedKey(res.apiKey);
      setMessage('Partner creado. Copiá la API key ahora: no se vuelve a mostrar.');
      await loadList();
      await loadDetail(res.id);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedId) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        webhookUrl: form.webhookUrl || null,
        notifyOn: form.notifyOn,
        defaultBranchId: form.defaultBranchId || null,
        productMap: productMapFromForm(),
        isActive: form.isActive,
      };
      if (form.webhookSecret.trim()) payload.webhookSecret = form.webhookSecret.trim();
      await apiService.updateAdminPartner(selectedId, payload);
      setMessage('Guardado');
      setForm((f) => ({ ...f, webhookSecret: '' }));
      await loadList();
      await loadDetail(selectedId);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const rotateKey = async () => {
    if (!selectedId) return;
    if (!window.confirm('¿Rotar API key? La anterior deja de funcionar.')) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiService.rotateAdminPartnerApiKey(selectedId);
      setRevealedKey(res.apiKey);
      setMessage('Nueva API key generada. Copiala ahora.');
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const ingestUrl =
    detail?.connection?.ingestUrl ||
    (detail ? `${apiBase}/api/v1/partners/${detail.code}/applications` : '');

  if (loading) return <p className="text-sm text-gray-500">Cargando integraciones…</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Integraciones (partners)</h2>
          <p className="text-sm text-gray-500 max-w-2xl">
            Sistemas satélite (Affinex, Credit Now créditos, etc.): API key de entrada, webhook de
            salida y mapeo de producto. Usá el bloque “Cómo conectar” para implementar el otro
            sistema.
          </p>
        </div>
        <button
          type="button"
          onClick={startCreate}
          className="px-4 py-2 bg-slate-800 text-white text-sm rounded-md hover:bg-slate-700"
        >
          Nuevo partner
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}
      {message && (
        <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
          {message}
        </div>
      )}
      {revealedKey && (
        <div className="p-4 rounded-md bg-amber-50 border border-amber-300 text-sm space-y-2">
          <p className="font-medium text-amber-900">API key (solo esta vez)</p>
          <code className="block break-all bg-white border rounded px-3 py-2 text-amber-950">
            {revealedKey}
          </code>
          <button
            type="button"
            className="text-amber-900 underline"
            onClick={() => navigator.clipboard.writeText(revealedKey)}
          >
            Copiar
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-[280px_1fr] gap-4">
        <div className="bg-white border rounded-lg overflow-hidden">
          <ul className="divide-y max-h-[480px] overflow-auto">
            {partners.length === 0 && (
              <li className="px-4 py-6 text-sm text-gray-500">No hay partners. Creá Affinex u otro.</li>
            )}
            {partners.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => loadDetail(p.id)}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-slate-50 ${
                    selectedId === p.id && !creating ? 'bg-slate-100' : ''
                  }`}
                >
                  <div className="font-medium text-gray-900">{p.name}</div>
                  <div className="text-xs text-gray-500 font-mono">{p.code}</div>
                  <div className="text-xs mt-1">
                    <span className={p.isActive ? 'text-emerald-700' : 'text-amber-700'}>
                      {p.isActive ? 'Habilitada' : 'Deshabilitada'}
                    </span>
                    {' · '}
                    notify {p.notifyOn}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border rounded-lg p-5 space-y-5">
          {!creating && !detail && (
            <p className="text-sm text-gray-500">Seleccioná un partner o creá uno nuevo.</p>
          )}

          {(creating || detail) && (
            <form onSubmit={creating ? saveCreate : saveUpdate} className="space-y-4 max-w-xl">
              <h3 className="font-semibold text-gray-900">
                {creating ? 'Nuevo partner' : `Editar · ${detail?.code}`}
              </h3>
              {creating && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Código (único)</label>
                  <input
                    required
                    value={form.code}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                    placeholder="affinex"
                    className="w-full px-3 py-2 border rounded-md font-mono text-sm"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-600 mb-1">Nombre</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Webhook URL (salida hacia el satélite)</label>
                <input
                  value={form.webhookUrl}
                  onChange={(e) => setForm((f) => ({ ...f, webhookUrl: e.target.value }))}
                  placeholder="https://affinex.../webhooks/creditnow"
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Webhook secret {detail?.hasWebhookSecret ? '(cargado · dejá vacío para no cambiar)' : ''}
                </label>
                <input
                  type="password"
                  value={form.webhookSecret}
                  onChange={(e) => setForm((f) => ({ ...f, webhookSecret: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  autoComplete="new-password"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Avisar al satélite cuando</label>
                  <select
                    value={form.notifyOn}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        notifyOn: e.target.value as 'APPROVED' | 'DISBURSED',
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="DISBURSED">Desembolso (recomendado Affinex)</option>
                    <option value="APPROVED">Aprobación del loan</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Sucursal default</label>
                  <select
                    value={form.defaultBranchId}
                    onChange={(e) => setForm((f) => ({ ...f, defaultBranchId: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="">—</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.code} · {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Producto PERSONAL</label>
                <select
                  value={form.personalProductId}
                  onChange={(e) => setForm((f) => ({ ...f, personalProductId: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">—</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.id})
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium">Integración habilitada</span>
                  <span className="block text-xs text-gray-500">
                    Si está apagada, no acepta ingest del satélite ni envía webhooks. El resto del
                    admin sigue funcionando.
                  </span>
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md disabled:opacity-50"
                >
                  {creating ? 'Crear' : 'Guardar'}
                </button>
                {!creating && (
                  <button
                    type="button"
                    onClick={rotateKey}
                    disabled={saving}
                    className="px-4 py-2 border border-amber-600 text-amber-800 text-sm rounded-md"
                  >
                    Rotar API key
                  </button>
                )}
              </div>
            </form>
          )}

          {detail && !creating && (
            <>
              <div className="border-t pt-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Cómo conectar (para Affinex / satélite)</h3>
                <div className="text-sm space-y-2 bg-slate-50 border rounded-md p-3 font-mono break-all">
                  <div>
                    <span className="text-gray-500">POST ingest</span>
                    <br />
                    {ingestUrl}
                  </div>
                  <div>
                    <span className="text-gray-500">Auth</span>
                    <br />
                    Authorization: Bearer &lt;API_KEY&gt;
                  </div>
                  <div>
                    <span className="text-gray-500">GET estado</span>
                    <br />
                    {`${apiBase}/api/v1/partners/${detail.code}/applications/:externalRef`}
                  </div>
                  <div>
                    <span className="text-gray-500">Eventos webhook</span>
                    <br />
                    {(detail.connection?.events ?? []).join(', ')}
                  </div>
                  <div>
                    <span className="text-gray-500">Firma</span>
                    <br />
                    {detail.connection?.signatureHeader ?? 'X-Signature HMAC-SHA256'}
                  </div>
                </div>
                <button
                  type="button"
                  className="text-sm text-blue-700 underline"
                  onClick={() => navigator.clipboard.writeText(ingestUrl)}
                >
                  Copiar URL ingest
                </button>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-2">Últimos webhooks enviados</h3>
                <div className="overflow-x-auto border rounded-md">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Cuándo</th>
                        <th className="px-3 py-2 text-left">Evento</th>
                        <th className="px-3 py-2 text-left">Ref</th>
                        <th className="px-3 py-2 text-left">Estado</th>
                        <th className="px-3 py-2 text-left">HTTP</th>
                        <th className="px-3 py-2 text-left">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {deliveries.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-3 py-4 text-gray-500">
                            Sin envíos aún
                          </td>
                        </tr>
                      )}
                      {deliveries.map((d) => (
                        <tr key={d.id}>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {new Date(d.createdAt).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{d.eventType}</td>
                          <td className="px-3 py-2 font-mono text-xs">{d.externalRef}</td>
                          <td className="px-3 py-2">{d.status}</td>
                          <td className="px-3 py-2">{d.responseCode ?? '—'}</td>
                          <td className="px-3 py-2 text-xs text-red-700 max-w-[200px] truncate">
                            {d.lastError ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
